import { useState, useEffect, useCallback } from 'react';
import { GalleryImage } from '@/mocks/cases';

interface GallerySectionProps {
  gallery: GalleryImage[];
  title: string;
}

export default function GallerySection({ gallery, title }: GallerySectionProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + gallery.length) % gallery.length);
  }, [lightboxIndex, gallery.length]);

  const goNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % gallery.length);
  }, [lightboxIndex, gallery.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, goPrev, goNext]);

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightboxIndex]);

  // Build grid layout: first item large, rest in varied sizes
  const [first, ...rest] = gallery;

  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: '#f5f3ef' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="block w-6 h-px bg-accent-teal" />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Deliverables</span>
            </div>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl md:text-3xl">
              制作物ギャラリー
            </h2>
          </div>
          <p className="hidden md:block font-sans-jp text-xs text-muted-light">
            {gallery.length}点の制作物 — クリックで拡大
          </p>
        </div>

        {/* Grid */}
        <div className="space-y-3">
          {/* First item — full width hero */}
          {first && (
            <button
              onClick={() => openLightbox(0)}
              className="group w-full overflow-hidden cursor-zoom-in relative block text-left"
            >
              <div className="relative overflow-hidden" style={{ height: '480px' }}>
                <img
                  src={first.url}
                  alt={first.caption}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-all duration-300" />
                {/* Zoom icon */}
                <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-warm-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <i className="ri-zoom-in-line text-charcoal text-sm" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 pb-2">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal/40 px-2 py-0.5">
                    {first.label}
                  </span>
                  <p className="font-sans-jp text-sm text-muted-brown">{first.caption}</p>
                </div>
                <span className="font-garamond text-xs italic text-muted-light">01 / {gallery.length}</span>
              </div>
            </button>
          )}

          {/* Rest — dynamic grid */}
          {rest.length > 0 && (
            <div className="grid grid-cols-12 gap-3">
              {rest.map((img, i) => {
                const globalIndex = i + 1;
                // Alternate layout patterns
                const isLandscape = img.aspect === 'landscape';
                const isPortrait = img.aspect === 'portrait';
                const colSpan = isLandscape ? 'col-span-12 md:col-span-8' : isPortrait ? 'col-span-12 md:col-span-4' : 'col-span-12 md:col-span-6';
                const imgHeight = isLandscape ? 'h-64 md:h-80' : isPortrait ? 'h-80 md:h-96' : 'h-64 md:h-72';

                return (
                  <button
                    key={globalIndex}
                    onClick={() => openLightbox(globalIndex)}
                    className={`group overflow-hidden cursor-zoom-in relative text-left ${colSpan}`}
                  >
                    <div className={`relative overflow-hidden ${imgHeight}`}>
                      <img
                        src={img.url}
                        alt={img.caption}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                      />
                      <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-all duration-300" />
                      <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-warm-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <i className="ri-zoom-in-line text-charcoal text-xs" />
                      </div>
                      {/* Label overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-charcoal/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-[10px] font-sans-jp text-warm-white tracking-widest-jp">{img.label}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 pb-1">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal/40 px-2 py-0.5">
                          {img.label}
                        </span>
                        <p className="font-sans-jp text-xs text-muted-brown truncate max-w-xs">{img.caption}</p>
                      </div>
                      <span className="font-garamond text-xs italic text-muted-light flex-shrink-0">
                        {String(globalIndex + 1).padStart(2, '0')} / {gallery.length}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <p className="mt-6 font-sans-jp text-xs text-muted-light text-center md:hidden">
          タップで拡大表示
        </p>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(30,28,26,0.96)' }}
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-warm-white/70 hover:text-warm-white transition-colors duration-200 cursor-pointer z-10"
          >
            <i className="ri-close-line text-2xl" />
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 font-garamond text-sm italic text-warm-white/50">
            {lightboxIndex + 1} / {gallery.length}
          </div>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 md:left-8 w-10 h-10 flex items-center justify-center text-warm-white/60 hover:text-warm-white transition-colors duration-200 cursor-pointer z-10"
          >
            <i className="ri-arrow-left-line text-2xl" />
          </button>

          {/* Image */}
          <div
            className="relative max-w-5xl w-full mx-16 md:mx-24"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={gallery[lightboxIndex].url}
              alt={gallery[lightboxIndex].caption}
              className="w-full max-h-[75vh] object-contain"
            />
            <div className="mt-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal/40 px-2 py-0.5">
                  {gallery[lightboxIndex].label}
                </span>
                <p className="font-sans-jp text-sm text-warm-white/70">
                  {gallery[lightboxIndex].caption}
                </p>
              </div>
            </div>
            {/* Thumbnail strip */}
            <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2">
              {gallery.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className={`flex-shrink-0 w-14 h-10 overflow-hidden transition-all duration-200 cursor-pointer ${
                    i === lightboxIndex ? 'ring-1 ring-accent-teal opacity-100' : 'opacity-40 hover:opacity-70'
                  }`}
                >
                  <img src={img.url} alt={img.label} className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 md:right-8 w-10 h-10 flex items-center justify-center text-warm-white/60 hover:text-warm-white transition-colors duration-200 cursor-pointer z-10"
          >
            <i className="ri-arrow-right-line text-2xl" />
          </button>
        </div>
      )}
    </section>
  );
}
